import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import './Auth.css';
import { Theme } from "../../theme/Theme";
import { commonStyles } from "../../utils/styles/CommonStyle";
import { Icon } from "@iconify/react";
import colors from "../../theme/colors";
import CommonButton from "../../components/common/CommonButton";
import { useDispatch, useSelector } from "react-redux";
import { SignInActioninitiate } from "../../redux/actions/auth/signInAction";
import { Loader } from "../../utils";
import { useFormik } from "formik";
import * as Yup from "yup";
import { phoneValidation, loginPasswordValidation } from "../../utils/validations";
import CommonModel from "../../components/common/CommonModel";
import AuthLayout from "../../components/common/AuthLayout";

export default function Login() {
  const [showPW, setShowPW] = useState(false);
  const [deactivatedOpen, setDeactivatedOpen] = useState(false);
  const [apiError, setApiError] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const isBusiness = location.pathname.includes("business");
  const [userType, setUserType] = useState(isBusiness ? "business" : "user");
  const [tabSide, setTabSide] = useState(isBusiness ? 1 : 0);

  useEffect(() => {
    const b = location.pathname.includes("business");
    setUserType(b ? "business" : "user");
    setTabSide(b ? 1 : 0);
  }, [location.pathname]);

  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.signIn);

  useEffect(() => {
    const msg = sessionStorage.getItem("sessionExpired");
    if (msg) {
      setApiError(msg);
      sessionStorage.removeItem("sessionExpired");
    }
  }, []);

  const formik = useFormik({
    initialValues: {
      phone_number: "",
      password: "",
    },
    validationSchema: Yup.object({
      phone_number: phoneValidation,
      password: loginPasswordValidation,
    }),
    onSubmit: async (values) => {
      setApiError("");
      const cleanPhone = values.phone_number.replace(/^\+91\s?/, '');
      const result = await dispatch(
        SignInActioninitiate({ phone_number: cleanPhone, password: values.password }, navigate)
      );
      if (result?.deactivated) {
        setDeactivatedOpen(true);
        return;
      }
      if (result?.unverified) {
        localStorage.setItem("auth_phone", result.phoneNumber || cleanPhone);
        navigate("/verify-otp");
        return;
      }
      if (!result?.success && result?.message) {
        setApiError(result.message);
      }
    },
  });

  return (
    <AuthLayout
      title="Welcome Back!"
      subtitle={userType === "business" ? "Sign in to your business account" : "Sign in to discover businesses near you"}
      showTabs={true}
      tabSide={tabSide}
      switchTabRoutePrefix="/login"
    >
      <form onSubmit={formik.handleSubmit} autoComplete="off" style={{ marginTop: 24 }} className="w-100">
        <input
          type="tel"
          name="phone_number"
          placeholder="Phone Number"
          autoComplete="tel"
          value={formik.values.phone_number}
          maxLength={10}
          onChange={(e) => {
            const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
            formik.setFieldValue('phone_number', val);
            if (apiError) setApiError("");
          }}
          onPaste={(e) => {
            e.preventDefault();
            const pasted = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 10);
            formik.setFieldValue('phone_number', pasted);
            if (apiError) setApiError("");
          }}
          onBlur={formik.handleBlur}
          style={{
            ...commonStyles.input,
            ...Theme.inputText,
            ...(formik.touched.phone_number && formik.errors.phone_number ? commonStyles.errorBorder : commonStyles.commonBorder)
          }}
        />
        {formik.touched.phone_number && formik.errors.phone_number && (
          <div style={Theme.errorText}>{formik.errors.phone_number}</div>
        )}

        <div style={{ position: 'relative', marginTop: 8, display: 'flex', alignItems: 'center' }}>
          <input
            type={showPW ? "text" : "password"}
            name="password"
            placeholder="Password"
            autoComplete="current-password"
            value={formik.values.password}
            onChange={(e) => { formik.handleChange(e); if (apiError) setApiError(""); }}
            onBlur={formik.handleBlur}
            style={{
              ...commonStyles.input,
              ...Theme.inputText,
              ...(formik.touched.password && formik.errors.password ? commonStyles.errorBorder : commonStyles.commonBorder)
            }}
          />
          <span style={{
              position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
              cursor: 'pointer', userSelect: 'none', marginTop: 4
            }}
            onClick={() => setShowPW(v => !v)}
            tabIndex={0}
            role="button"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowPW(v => !v);
              }
            }}
            aria-label={showPW ? "Hide password" : "Show password"}
          >
            <Icon
              icon={showPW ? "qlementine-icons:eye-crossed-16" : "mdi-light:eye"}
              width="20"
              height="20"
              style={{ marginBottom: 6, color: "var(--text-secondary)" }}
            />
          </span>
        </div>
        {formik.touched.password && formik.errors.password && (
          <div style={Theme.errorText}>{formik.errors.password}</div>
        )}

        {apiError && (
          <div style={{
            marginTop: 10, padding: '10px 14px', backgroundColor: 'var(--color-danger-subtle, #fee2e2)',
            border: '1px solid var(--color-danger-border, #fca5a5)', borderRadius: 8,
            ...Theme.font14Regular, color: 'var(--color-danger-dark)', lineHeight: 1.5,
          }}>
            {apiError}
          </div>
        )}

        <div className="d-flex justify-content-end mt-4 mb-2">
          <Link to="/forgot-password" style={{ ...Theme.font14Medium, textDecoration: 'none', color: colors.mainColor }}>
            Forgot Password?
          </Link>
        </div>
        <div className="d-flex justify-content-center mx-auto mb-2">
          <CommonButton
            title={
              loading ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <Loader variant="dots" size="small" color="white" />
                  <span>Logging in...</span>
                </div>
              ) : "Log In"
            }
            type="submit"
            width={"100%"}
            disabled={loading}
          />
        </div>
      </form>

      <div style={{ marginTop: 28 }} className="w-100">
        <span style={Theme.modalDescription} className="d-flex justify-content-center">Don't have an account?</span>
        <Link to={userType === "business" ? "/business/signup" : "/signup"} style={{ display: 'inline-block', width: '100%' }} className="text-decoration-none">
          <CommonButton
            title="Sign Up Now!"
            onClick={() => { }}
            width="100%"
            variant="outline"
          />
        </Link>
      </div>

      <CommonModel
        isOpen={deactivatedOpen}
        onClose={() => setDeactivatedOpen(false)}
        title="Account Deactivated"
        width={420}
        height="auto"
        needCrossIcon={true}
      >
        <div style={{ textAlign: "center", paddingBottom: 8 }}>
          <div style={{
            width: 72, height: 72, borderRadius: "50%",
            background: "var(--color-warning-bg)", border: "2px solid var(--color-gold, #eab308)",
            display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px",
          }}>
            <Icon icon="ph:lock-simple-bold" width={36} color="var(--color-warning-amber, #f59e0b)" />
          </div>
          <p style={{
            ...Theme.font16Regular, color: "var(--text-secondary)", lineHeight: 1.7, marginBottom: 20,
          }}>
            Your account has been{" "}
            <span style={{ color: "var(--color-warning-amber, #f59e0b)", fontWeight: 700 }}>deactivated</span>.
            <br />
            Please contact support or the administrator for assistance.
          </p>
          <div style={{
            background: "var(--bg-surface-2)", border: "1px solid var(--border-subtle)", borderRadius: 12,
            padding: "14px 18px", marginBottom: 24, display: "flex", alignItems: "flex-start", gap: 10, textAlign: "left",
          }}>
            <Icon icon="ph:headset-bold" width={20} color={colors.themeColor} style={{ flexShrink: 0, marginTop: 2 }} />
            <span style={{ ...Theme.font14Regular, color: "var(--text-secondary)", lineHeight: 1.6 }}>
              If you believe this is a mistake, please reach out to our support team to resolve your account status.
            </span>
          </div>
          <CommonButton
            title="OK, Got It"
            onClick={() => setDeactivatedOpen(false)}
            width="100%"
          />
        </div>
      </CommonModel>
    </AuthLayout>
  );
}
